import { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import CarouselPauseButton from './CarouselPauseButton';
import badgeMariagesNet from '@/assets/badge-mariages-net.png';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  text: string;
  rating: number;
}

interface Props {
  testimonials: Testimonial[];
}

const AUTOPLAY_DELAY = 5000;

const Temoignages = ({ testimonials }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [paused, setPaused] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', slidesToScroll: 1 });

  useEffect(() => {
    if (!emblaApi || paused) return;
    const interval = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_DELAY);
    const stop = () => clearInterval(interval);
    emblaApi.on('pointerDown', stop);
    return () => { clearInterval(interval); emblaApi.off('pointerDown', stop); };
  }, [emblaApi, paused]);

  const onSelect = useCallback(() => { if (emblaApi) setSelectedIndex(emblaApi.selectedScrollSnap()); }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  return (
    <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground uppercase tracking-wide">Ce que disent nos clients</h2>
          <div className="flex items-center justify-center gap-4 mt-3">
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={18} className="fill-amber-400 text-amber-400" />))}</div>
              <span className="text-sm font-semibold text-foreground">4,9/5</span>
              <a href="https://maps.app.goo.gl/SPqj82CAvgbDAqUU6" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors">— 32 avis Google</a>
            </div>
            <a href="https://www.mariages.net/organisation-mariage/cygnature-production--e311506" target="_blank" rel="noopener noreferrer">
              <img src={badgeMariagesNet} alt="Recommandé sur Mariages.net" className="h-14 w-auto" width={56} height={56} loading="lazy" />
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <button onClick={scrollPrev} className="shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all" aria-label="Précédent"><ChevronLeft size={18} /></button>
          <div className="overflow-hidden flex-1 min-w-0" ref={emblaRef}>
            <div className="flex -ml-4 pr-1">
              {testimonials.map((t, idx) => (
                <div key={idx} className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] pl-4">
                  <div className="p-6 rounded-2xl border border-border bg-background h-full flex flex-col">
                    <div className="flex gap-0.5 mb-4">{Array.from({ length: t.rating }).map((_, i) => (<Star key={i} size={18} className="fill-amber-400 text-amber-400" />))}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed italic flex-1">"{t.text}"</p>
                    <div className="mt-5 flex items-center gap-3 pt-4 border-t border-border">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-heading font-bold text-sm shrink-0">{t.name.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-heading font-semibold text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.company}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button onClick={scrollNext} className="shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all" aria-label="Suivant"><ChevronRight size={18} /></button>
        </div>

        <div className="flex items-center justify-center gap-3 mt-8">
          <div className="flex items-center gap-1.5">
            {scrollSnaps.slice(0, 6).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === selectedIndex % 6 ? 'bg-primary' : 'bg-border'}`}
                aria-label={`Page ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Temoignages;
